/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {SecurityAgent} from "../e2ee/SecurityAgent.js";
import {Utils} from "../utils/Utils.js";
import {Log} from "../utils/Log.js";
import {HubsInfoCache} from "./HubsInfoCache.js";

/**
 * HubInfo is the client-side handle for a remote Hub.
 *
 * It owns:
 * - discovery + reachability checks
 * - E2EE key exchange (Diffie-Hellman) and AES key association
 * - local caching to avoid repeated key exchanges
 */
export class HubInfo {

    /**
     * Default host is derived from the browser origin; undefined in non-browser runtimes to force explicit configuration.
     *
     * @returns {string|undefined}
     */
    static defaultHost() {
        if (typeof window === "undefined") {
            return undefined;
        } else {
            return window.location.origin;
        }
    }


    /**
     * @type {string}
     */
    UUID = ""

    /**
     * @type {string}
     */
    name = ""

    /**
     * @type {string}
     */
    code = ""

    /**
     * @type {string}
     */
    host = ""

    /**
     * @var CryptoKey
     */
    aesKey;



    ////////////////////////////////////////
    //  Static and designated constructors
    ////////////////////////////////////////


    /**
     * Uses the cache when possible to avoid repeating the key exchange (which is both expensive and rate-limit sensitive).
     * @param {string} name
     * @param {string} code
     * @param {string|undefined} host
     * @returns {Promise<HubInfo>}
     * @throws {Error}
     */
    static async get(name, code = "", host = HubInfo.defaultHost()) {
        const cachedHubInfos = HubsInfoCache.get(name, host);
        if (cachedHubInfos) {
            if (Log.supports(Log.Core)) {
                Log.core("[HubInfo.get] HubsInfoCache.get found", cachedHubInfos.name);
            }
            return Promise.resolve(cachedHubInfos);
        }

        // Ensure the hub is reachable before performing key exchange to reduce noisy failures during startup races.
        await HubInfo.waitToBeAlive(name,host)

        const hubInfosUUID = Utils.v4UUID();
        const o = await HubInfo._getSharedKey(hubInfosUUID, name, code, host);
        const hubInfos = new HubInfo(hubInfosUUID, name, code, host, o.aesKey)
        return HubsInfoCache.cache(hubInfos);
    }


    /**
     * Creates a remote hub then returns a client-side handle bound to the provided AES key.
     * @param {string} name
     * @param {string} code
     * @param {CryptoKey|string} aesKey
     * @param {string|undefined} host
     * @returns {Promise<HubInfo>}
     */
    static async createHub(name, code = "", aesKey, host = HubInfo.defaultHost()) {

        const hubInfosUUID = Utils.v4UUID();

        if (HubsInfoCache.get(name, host)) {
            return Promise.reject(new Error("attempt to create a Hub that is already existing and cached"));
        }
        if (aesKey !== SecurityAgent.NO_AES_KEY) {
            if (!(aesKey instanceof CryptoKey)) {
                return Promise.reject(new Error("Hub AES key is required"));
            }
        }


        let e2eeTempCode = Utils.v4UUID(); // Temporary code used only for the initial E2EE bootstrap exchange.
        let o = await HubInfo._getSharedKey(hubInfosUUID, name, e2eeTempCode, host);
        let storageIndex = o.storageIndex;
        let tempAesSymKey = o.aesKey

        let base64EncodedHubAESKey = await SecurityAgent.convertCryptoKeyToBase64(aesKey);

        /**
         * Creator-provided AES key is authoritative; consumers must not assume the hub will generate one server-side.
         * @type {{hubName: string, hubCode: string, aesKey: (CryptoKey|string)}}
         */
        const createHubPayload = {
            hubName: name,
            hubCode: code,
            aesKey: base64EncodedHubAESKey
        };

        const response = await SecurityAgent.fetch(hubInfosUUID, tempAesSymKey, `${host}/api/v1/actors/create/hub/${Utils.hexEncodeToString(storageIndex)}/${e2eeTempCode}`, {
            method: 'POST',
            body: JSON.stringify(createHubPayload),
            headers: HubInfo.httpHeaders()
        });

        if (response.ok) {
            const hubInfos = new HubInfo(hubInfosUUID, name, code, host, aesKey);
            return HubsInfoCache.cache(hubInfos);
        } else {
            const j = await response.json();
            return Promise.reject(new Error(j.message || "Unknown error"));
        }
    }


    /**
     * @param {string}hubInfosUUID
     * @param {string} name
     * @param {string} code
     * @param {string} host
     * @return {Promise<{aesKey: (CryptoKey|string), storageIndex: string}>}
     * @private
     */
    static async _getSharedKey(hubInfosUUID, name, code = "", host = HubInfo.defaultHost()) {
        if (Log.supports(Log.Core)) {
            Log.core("[HubInfo._getSharedKey", hubInfosUUID, name);
        }
        const keyExchangeEndpoint = `${host}/api/v1/security/exchange/key`;
        const distantHubUrl = `${host}/api/v1/actors/sse/subscribe/to/${name}`;
        return await SecurityAgent.getSymmetricKey(hubInfosUUID, keyExchangeEndpoint, distantHubUrl, code);
    }


    /**
     * @param {string} UUID
     * @param {string} name
     * @param {string} code
     * @param {string} host
     * @param {CryptoKey|string} aesKey
     * @throws {Error}
     */
    constructor(UUID, name, code, host = HubInfo.defaultHost(), aesKey) {
        this.UUID = UUID;
        this.name = name;
        this.code = code;
        this.host = host;
        if (aesKey instanceof CryptoKey) {
            this.aesKey = aesKey;
        } else {
            throw new Error("aesKey must be an instance of CryptoKey");
        }
    }

    /**
     * Drop cached key material for this hub to force a fresh key exchange on next access.
     */
    forget() {
        HubsInfoCache.forget(this);
        this.UUID = "";
        this.name = "";
        this.code = "";
        this.host = "";
    }


    /////////////////////
    // Reachability    //
    // and waiting     //
    /////////////////////

    /**
     * @param {string} hubName
     * @param {string} [host=defaultHost()]
     * @returns {Promise<boolean>}
     */
    static async isAlive(hubName, host = HubInfo.defaultHost()) {
        const api = SecurityAgent.apiHostFrom(host);
        const isAliveEndPoint = `${api}/api/v1/actors/hub/is/alive/${hubName}/0`;
        try {
            const response = await fetch(isAliveEndPoint);
            return response.ok;
        } catch (e) {
            return false;
        }
    }

    /**
     * @param {string} hubName
     * @param {string} [host=defaultHost()]
     * @param {number} [maxRetries=50]
     * @param {number} [timeout=5000]
     * @returns {Promise<void>}
     */
    static async waitToBeAlive(hubName, host = HubInfo.defaultHost(), maxRetries = 50, timeout = 5000) {
        return await HubInfo.waitForConnections(hubName, host, 0, maxRetries, timeout);
    }

    /**
     * @param {string} hubName
     * @param {string} [host=defaultHost()]
     * @param {number} [nb=0]
     * @param {number} [maxRetries=50]
     * @param {number} [timeout=5000]
     * @returns {Promise<void>}
     */
    static async waitForConnections(hubName, host = HubInfo.defaultHost(), nb = 0, maxRetries = 50, timeout = 5000) {
        const api = SecurityAgent.apiHostFrom(host);
        const isAliveEndPoint = `${api}/api/v1/actors/hub/is/alive/${hubName}/${nb}`;
        return await Utils.waitForReachabilityOf(isAliveEndPoint, maxRetries, timeout);
    }

    /**
     * @returns {Promise<boolean>}
     */
    async isAlive() {
        return await HubInfo.isAlive(this.name, this.host);
    }

    /**
     * @param {number} [maxRetries=50]
     * @param {number} [timeout=5000]
     * @returns {Promise<void>}
     */
    async waitToBeAlive(maxRetries = 50, timeout = 5000) {
        return await HubInfo.waitToBeAlive(this.name, this.host, maxRetries, timeout);
    }

    /**
     * @param {number} [nb=0]
     * @param {number} [maxRetries=50]
     * @param {number} [timeout=5000]
     * @returns {Promise<void>}
     */
    async waitForConnections(nb = 0, maxRetries = 50, timeout = 5000) {
        return await HubInfo.waitForConnections(this.name, this.host, nb, maxRetries, timeout);
    }

    /**
     * Ensures JSON defaults for hub REST calls while allowing callers to override.
     * @param headers {Headers|undefined|Object.<string, string>}
     * @returns Headers
     */
    static httpHeaders(headers = undefined) {
        if (headers === undefined) {
            headers = new Headers({});
        }
        const httpHeaders = headers instanceof Headers ? headers : new Headers(headers);
        if (!'Content-Type' in headers) {
            httpHeaders.set("Content-Type", "application/json");
        }
        return httpHeaders;
    }

}